import { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Snackbar, Typography } from "@mui/material";
import {
  useConfirmInvocation,
  useConversation,
  useConversations,
  useCreateConversation,
  useDeleteConversation,
  useRejectInvocation,
  useSendMessage,
} from "@/hooks/usePersonalAgent";
import ConversationListPanel from "@/components/personal-agent/ConversationListPanel";
import ConversationTitle from "@/components/personal-agent/ConversationTitle";
import MessageList from "@/components/personal-agent/MessageList";
import MessageInput from "@/components/personal-agent/MessageInput";

export default function MyAssistant() {
  const { data: conversations = [], isLoading: listLoading } = useConversations();
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const { data: detail, isLoading: detailLoading } = useConversation(selectedId);

  const createMutation = useCreateConversation();
  const deleteMutation = useDeleteConversation();
  const sendMutation = useSendMessage(selectedId);
  const confirmMutation = useConfirmInvocation(selectedId);
  const rejectMutation = useRejectInvocation(selectedId);

  const [busyInvocationId, setBusyInvocationId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  // Auto-select the most recent conversation when the list first loads.
  useEffect(() => {
    if (!selectedId && conversations.length > 0) {
      setSelectedId(conversations[0].id);
    }
  }, [conversations, selectedId]);

  const handleNew = async () => {
    try {
      const c = await createMutation.mutateAsync({});
      setSelectedId(c.id);
    } catch {
      setSnackbar({ open: true, message: "Failed to create conversation", severity: "error" });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      if (id === selectedId) setSelectedId(undefined);
    } catch {
      setSnackbar({ open: true, message: "Failed to delete conversation", severity: "error" });
    }
  };

  const handleSend = async (content: string) => {
    if (!selectedId) return;
    try {
      const result = await sendMutation.mutateAsync(content);
      if (result.halted_on_pending) {
        setSnackbar({
          open: true,
          message: "The agent wants to run a write action — review and approve below.",
          severity: "success",
        });
      }
    } catch {
      setSnackbar({ open: true, message: "Failed to send message", severity: "error" });
    }
  };

  const handleApprove = async (invocationId: string, params: Record<string, unknown>) => {
    setBusyInvocationId(invocationId);
    try {
      await confirmMutation.mutateAsync({ invocationId, params });
      setSnackbar({ open: true, message: "Action approved and executed", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to approve action", severity: "error" });
    } finally {
      setBusyInvocationId(null);
    }
  };

  const handleReject = async (invocationId: string) => {
    setBusyInvocationId(invocationId);
    try {
      await rejectMutation.mutateAsync(invocationId);
      setSnackbar({ open: true, message: "Action rejected", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to reject action", severity: "error" });
    } finally {
      setBusyInvocationId(null);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        // Fill the content area inside Layout's `p: 3` padding. The earlier
        // `m: -3` edge-to-edge hack pushed the box 24px past the viewport
        // on each side and the page got a horizontal scrollbar — drop it,
        // accept the 24px gutters, content stays on screen.
        height: "calc(100vh - 64px - 48px)",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <ConversationListPanel
        conversations={conversations}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onNew={handleNew}
        onDelete={handleDelete}
        loading={createMutation.isPending}
      />

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {!selectedId && (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="body1" color="text.secondary">
              {listLoading ? <CircularProgress /> : "Pick a conversation or start a new one."}
            </Typography>
          </Box>
        )}

        {selectedId && detailLoading && (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        )}

        {selectedId && detail && (
          <>
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
              <ConversationTitle
                conversationId={detail.conversation.id}
                title={detail.conversation.title}
              />
            </Box>
            <MessageList
              messages={detail.messages}
              onApprove={handleApprove}
              onReject={handleReject}
              busyInvocationId={busyInvocationId}
              pending={sendMutation.isPending}
            />
            <MessageInput
              onSend={handleSend}
              disabled={sendMutation.isPending}
              placeholder={
                sendMutation.isPending
                  ? "Thinking…"
                  : "Ask anything — I can read your inbox, calendar, Drive…"
              }
            />
          </>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
